import { VideoSourceSettings } from "../../../../../components/VideoSourceSettings";
import React, { useState } from "react";
import {
  BLOCK_TYPES,
  BRAND_DARK,
  BTN_GHOST,
  BUTTON_RESET,
  INPUT,
  LABEL,
} from "../../../../../styles";
import { FileIcon } from "../../../../../components/FileIcon";
import { HtmlContentFrame } from "../../../../../components/HtmlContentFrame";
import { fileToDataUrl, uuid } from "../../../../../shared/utils";
import { sourceImage } from "../../../../../utils/source-image";
import { uploadSimuladorEmbedHtml } from "../../../../../services/simuladoresConteudoApi";
import { RichTextEditor } from "./RichTextEditor";

// Acima deste tamanho, o HTML deve ser enviado como arquivo estatico
// (evita guardar dezenas de MB em conteudo_guia_blocos.html e travar a API).
const HTML_INLINE_WARN_SIZE = 200 * 1024;

export function BlockEditor({ bloco, onChange, onRemove }) {
  const [arquivoNome, setArquivoNome] = useState("");
  const [arquivoUrl, setArquivoUrl] = useState("");
  const [enviandoHtml, setEnviandoHtml] = useState(false);
  const [enviandoArquivos, setEnviandoArquivos] = useState(false);
  const tipoInfo = BLOCK_TYPES.find((item) => item.tipo === bloco.tipo);
  const htmlSize = new Blob([bloco.html || ""]).size;

  return (
    <div
      style={{
        border: "1px solid #E7EEF2",
        borderRadius: 12,
        overflow: "hidden",
        background: "#FFF",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "9px 12px",
          background: tipoInfo?.fundo || "#F8FAFC",
          borderBottom: "1px solid #E7EEF2",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              color: tipoInfo?.cor || BRAND_DARK,
            }}
          >
            {tipoInfo?.label}
          </div>
          <div style={{ fontSize: 10, color: "#64748B" }}>
            {tipoInfo?.descricao}
          </div>
        </div>
        <button
          onClick={() => onRemove(bloco)}
          style={{
            ...BUTTON_RESET,
            background: "transparent",
            color: "#DC2626",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          Remover
        </button>
      </div>
      <div style={{ padding: 12 }}>
        {bloco.tipo === "texto" && (
          <RichTextEditor
            value={bloco.html || ""}
            onChange={(html) => onChange({ ...bloco, html })}
            placeholder="Escreva o conteudo, aplique negrito, titulos, listas, links e imagens."
          />
        )}
        {bloco.tipo === "html" && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <label
                style={{
                  ...BTN_GHOST,
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: enviandoHtml ? "wait" : "pointer",
                  opacity: enviandoHtml ? 0.6 : 1,
                }}
              >
                {enviandoHtml ? "Enviando..." : "Enviar arquivo HTML"}
                <input
                  type="file"
                  accept=".html,.htm,text/html"
                  style={{ display: "none" }}
                  disabled={enviandoHtml}
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (!file) return;

                    setEnviandoHtml(true);
                    try {
                      const resultado = await uploadSimuladorEmbedHtml(file);
                      const url = resultado?.url;
                      if (!url) {
                        throw new Error("Upload sem URL retornada");
                      }
                      const src = sourceImage(url);
                      onChange({
                        ...bloco,
                        html: `<iframe src="${src}" style="width:100%;min-height:600px;border:none;" scrolling="yes"></iframe>`,
                      });
                    } catch {
                      window.alert(
                        "Nao foi possivel enviar o arquivo HTML. Tente novamente.",
                      );
                    } finally {
                      setEnviandoHtml(false);
                    }
                  }}
                />
              </label>
              <span style={{ fontSize: 10.5, color: "#64748B" }}>
                Use para arquivos grandes (paginas interativas, relatorios).
              </span>
            </div>
            {htmlSize > HTML_INLINE_WARN_SIZE && (
              <div
                style={{
                  marginBottom: 8,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#FEF3C7",
                  color: "#92400E",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Este bloco tem {(htmlSize / 1024 / 1024).toFixed(1)} MB de HTML
                colado direto no texto. Prefira "Enviar arquivo HTML" acima para
                conteudos grandes, senao a tela pode travar ou cortar ao
                carregar o curso.
              </div>
            )}
            <textarea
              value={bloco.html || ""}
              onChange={(event) =>
                onChange({ ...bloco, html: event.target.value })
              }
              style={{
                ...INPUT,
                minHeight: 140,
                resize: "vertical",
                background: "#0F172A",
                color: "#A7F3D0",
                fontFamily: "monospace",
                fontSize: 12,
              }}
              placeholder="Cole um iframe, embed ou HTML da aula."
            />
            {bloco.html && (
              <div
                style={{
                  marginTop: 10,
                  border: "1px solid #E7EEF2",
                  borderRadius: 12,
                  padding: 12,
                  background: "#F8FAFC",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 900,
                    color: "#64748B",
                    marginBottom: 8,
                  }}
                >
                  Preview
                </div>
                <HtmlContentFrame
                  html={bloco.html}
                  title="Preview do conteudo HTML"
                  instanceKey={`admin-preview:simulador:${bloco.id || "novo"}`}
                />
              </div>
            )}
          </div>
        )}
        {bloco.tipo === "anexo" && (
          <div>
            <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
              {(bloco.arquivos || []).map((arquivo, index) => (
                <div
                  key={arquivo.id || index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "#F8FBFC",
                    border: "1px solid #E4EDF2",
                    borderRadius: 12,
                    padding: 10,
                  }}
                >
                  <FileIcon ext={arquivo.ext} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#18212B",
                      }}
                    >
                      {arquivo.nome}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748B" }}>
                      {arquivo.tamanho || arquivo.url}
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      onChange({
                        ...bloco,
                        arquivos: bloco.arquivos.filter(
                          (_, itemIndex) => itemIndex !== index,
                        ),
                      })
                    }
                    style={{
                      ...BUTTON_RESET,
                      background: "transparent",
                      color: "#DC2626",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <label style={LABEL}>Upload rapido</label>
              <label
                style={{
                  display: "grid",
                  placeItems: "center",
                  border: "2px dashed #D6E0E5",
                  borderRadius: 12,
                  minHeight: 72,
                  color: "#64748B",
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: enviandoArquivos ? "wait" : "pointer",
                  opacity: enviandoArquivos ? 0.6 : 1,
                  marginBottom: 8,
                }}
              >
                {enviandoArquivos ? "Enviando..." : "Escolher arquivos"}
                <input
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  disabled={enviandoArquivos}
                  onChange={async (event) => {
                    const files = Array.from(event.target.files || []);
                    event.target.value = "";
                    if (!files.length) {
                      return;
                    }

                    setEnviandoArquivos(true);
                    try {
                      const novosArquivos = await Promise.all(
                        files.map(async (file) => {
                          const size =
                            file.size > 1024 * 1024
                              ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
                              : `${Math.max(1, Math.round(file.size / 1024))} KB`;
                          const dataUrl = await fileToDataUrl(file);

                          return {
                            id: uuid(),
                            nome: file.name,
                            url: dataUrl,
                            tamanho: size,
                            ext:
                              file.name.split(".").pop()?.toLowerCase() || "",
                          };
                        }),
                      );

                      onChange({
                        ...bloco,
                        arquivos: [
                          ...(bloco.arquivos || []),
                          ...novosArquivos,
                        ],
                      });
                    } finally {
                      setEnviandoArquivos(false);
                    }
                  }}
                />
              </label>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                <input
                  value={arquivoNome}
                  onChange={(event) => setArquivoNome(event.target.value)}
                  style={INPUT}
                  placeholder="Nome do arquivo"
                />
                <input
                  value={arquivoUrl}
                  onChange={(event) => setArquivoUrl(event.target.value)}
                  style={INPUT}
                  placeholder="URL publica"
                />
              </div>
              <div>
                <button
                  onClick={() => {
                    if (!arquivoNome.trim()) {
                      return;
                    }

                    onChange({
                      ...bloco,
                      arquivos: [
                        ...(bloco.arquivos || []),
                        {
                          id: uuid(),
                          nome: arquivoNome.trim(),
                          url: arquivoUrl.trim() || "#",
                          tamanho: "",
                          ext:
                            arquivoNome.split(".").pop()?.toLowerCase() || "",
                        },
                      ],
                    });
                    setArquivoNome("");
                    setArquivoUrl("");
                  }}
                  style={BTN_GHOST}
                >
                  Adicionar link
                </button>
              </div>
            </div>
          </div>
        )}
        {bloco.tipo === "video" && (
          <>
          <input
            value={bloco.html || ""}
            onChange={(event) =>
              onChange({ ...bloco, html: event.target.value })
            }
            style={{
              ...INPUT,
              minHeight: 40,
              resize: "vertical",
              background: "#0F172A",
              color: "#A7F3D0",
              fontFamily: "monospace",
              fontSize: 12,
            }}
            placeholder="Cole a URL do video (YouTube, Vimeo, etc)."
          />
          <VideoSourceSettings src={bloco.html} />
          </>
        )}
      </div>
    </div>
  );
}
